import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Paper, Stack, Typography } from '@mui/material';

import useLocalization from '../../hooks/useLocalization';
import useMarketplaceCurrency from '../../hooks/usePlayerCurrency';
import type { FirstDepositBonusResponse } from '../../types/bonus';
import { BonusProgramType, CampaignProduct } from '../../types/bonus';
import { formatBonusAmount } from '../../utils/bonus';

const styles = {
  root: {
    px: 1.5,
    py: 1.25,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    borderRadius: 1.5,
    border: '1px solid',
    borderColor: 'error.light',
    backgroundColor: 'neutral.600',
    color: 'neutral.200',
  },
  iconContainer: {
    width: 32,
    height: 32,
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
  },
  icon: {
    color: 'neutral.300',
    fontSize: 20,
  },
  content: {
    minWidth: 0,
    gap: 0.25,
  },
  title: {
    color: 'neutral.50',
    fontWeight: 700,
    lineHeight: 1.25,
  },
  label: {
    color: 'error.200',
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.25,
    textTransform: 'uppercase',
  },
  amount: {
    ml: 'auto',
    flexShrink: 0,
    color: 'neutral.25',
    fontWeight: 800,
  },
} as const;

type Props = {
  bonus: FirstDepositBonusResponse | null | undefined;
};

const bonusTitleKeys: Record<BonusProgramType, Record<CampaignProduct, string>> = {
  [BonusProgramType.FIRST_TIME_DEPOSIT]: {
    [CampaignProduct.SPORTSBOOK]: 'firstDepositBonus',
    [CampaignProduct.CASINO]: 'firstDepositBonus',
    [CampaignProduct.ALL]: 'firstDepositBonus',
  },
  [BonusProgramType.WELCOME_BONUS]: {
    [CampaignProduct.SPORTSBOOK]: 'welcomeBonus.sportsbookTitle',
    [CampaignProduct.CASINO]: 'welcomeBonus.casinoTitle',
    [CampaignProduct.ALL]: 'welcomeBonus.sportsbookTitle',
  },
};

const BonusMinimumDepositNotice = ({ bonus }: Props) => {
  const { t, i18n } = useLocalization();
  const currency = useMarketplaceCurrency();

  const isInvalidBonus =
    !bonus ||
    bonus.status !== null ||
    bonus.minDepositAmount <= 0 ||
    (!!bonus.expiryDate && new Date(bonus.expiryDate) < new Date());

  if (isInvalidBonus) {
    return null;
  }

  const bonusTitle = t(bonusTitleKeys[bonus.bonusProgramType][bonus.campaignProduct]);

  return (
    <Paper elevation={0} sx={styles.root} data-testid='bonus-minimum-deposit-notice'>
      <Box sx={styles.iconContainer}>
        <InfoOutlinedIcon sx={styles.icon} />
      </Box>
      <Stack sx={styles.content}>
        <Typography variant='body3' sx={styles.title}>
          {bonusTitle}
        </Typography>
        <Typography variant='caption' sx={styles.label}>
          {t('minimumDeposit')}
        </Typography>
      </Stack>
      <Typography variant='h5' sx={styles.amount}>
        {formatBonusAmount(bonus.minDepositAmount, currency, i18n.language)}
      </Typography>
    </Paper>
  );
};

export default BonusMinimumDepositNotice;
